import { Component, EventEmitter, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@services/support/snackbar.service';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Workflow } from 'src/app/models/Workflow';
import { LoaderService } from 'src/app/services/support/loader.service';
import { WorkflowService } from 'src/app/services/workflow.service';

@Component({
    selector: 'app-workflow-sharing-modal',
    templateUrl: './workflow-sharing-modal.component.html',
    styleUrls: ['./workflow-sharing-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class WorkflowSharingModalComponent implements OnInit {
  onClose: EventEmitter<any> = new EventEmitter();

  @Input('workflow') workflow!: Workflow;

  // Sharing with child accounts
  childAccounts: {id: string, name: string}[] = [];
  sharingSelection: { [accountId: string]: boolean } = {};
  isParentAccount: boolean = false;
  isLoading: boolean = true;

  constructor(
    private workflowService: WorkflowService,
    private modalRef: BsModalRef,
    private loaderService: LoaderService,
    private translateService: TranslateService,
    private snackBarService: SnackbarService
  ) { }

  ngOnInit(): void {
    this.loadSharingData();
  }

  loadSharingData() {
    this.isLoading = true;
    this.loaderService.show();
    this.workflowService.getAccountOrganization().subscribe(
      org => {
        this.loaderService.hide();
        this.isLoading = false;
        
        if (org && org.is_master === true && org.child_account_ids && org.child_account_ids.length > 0) {
          this.isParentAccount = true;
          
          // Use child_accounts if available (has id and name), otherwise use child_account_ids
          if (org.child_accounts && Array.isArray(org.child_accounts)) {
            this.childAccounts = org.child_accounts;
          } else {
            // Fallback to child_account_ids (only IDs)
            this.childAccounts = org.child_account_ids.map(id => ({ id, name: id }));
          }
          
          // Set current selection from workflow
          const currentShared = this.workflow.shared_with_children || [];
          this.sharingSelection = {};
          currentShared.forEach(id => this.sharingSelection[id] = true);
        }
      },
      error => {
        this.loaderService.hide();
        this.isLoading = false;
        console.error('Error loading organization:', error);
      }
    );
  }

  saveSharing() {
    const selectedChildren = Object.entries(this.sharingSelection)
      .filter(([_, selected]) => selected)
      .map(([id, _]) => id);
    
    this.loaderService.show();
    this.workflowService.updateWorkflowSharing(this.workflow.id, selectedChildren)
      .then(() => {
        this.snackBarService.success(this.translateService.instant('MODALS.WORKFLOW-SHARING.SHARING_UPDATED'));
        this.workflow.shared_with_children = selectedChildren;
        this.onClose.next(selectedChildren);
        this.modalRef.hide();
      })
      .catch(err => {
        console.error('Failed to update sharing:', err);
        this.snackBarService.error(this.translateService.instant('MODALS.WORKFLOW-SHARING.SHARING_ERROR'));
      })
      .finally(() => this.loaderService.hide());
  }

  close() {
    this.modalRef.hide();
  }
}
